import { Box, Drawer, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateFolderMutation } from 'api/mutations/useUpdateFolderMutation';
import { useWindowSize } from 'hooks';

import { CancelButton, Dialog, DuplicateButton, FooterActions, StyledTextField } from './styled';

interface EditCategoryModalProps {
  open: boolean;
  onClose: () => void;
  itemName?: string;
  itemDescription?: string;
  folderId?: string;
  update?: boolean;
}

export function FolderEditCategoryModal({
  open,
  onClose,
  itemName = '',
  itemDescription = '',
  folderId,
  update,
}: EditCategoryModalProps) {
  const [name, setName] = useState(itemName);

  const { updateFolderMutation } = useUpdateFolderMutation();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (open) {
      setName(itemName);
    }
  }, [open, itemName, itemDescription]);

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleDuplicate = async () => {
    if (update && folderId) {
      await updateFolderMutation({ id: folderId, name });
    }
    handleClose();
  };

  const formContent = (
    <Box sx={{ px: 2, pb: 'env(safe-area-inset-bottom)' }}>
      <Typography variant="h6" fontWeight={700} fontSize="16px" mb={3}>
        {update
          ? t('resources.documentCategories.folders.editModal.title')
          : t('resources.documentCategories.folders.duplicateModal.title')}
      </Typography>

      <Box mb={2}>
        <Typography
          variant="body2"
          fontSize="14px"
          fontWeight={500}
          color="text.secondary"
          mb={0.75}
        >
          {t('resources.documentCategories.folders.editModal.folderName')}
        </Typography>
        <StyledTextField
          fullWidth
          placeholder={t('resources.documentCategories.folders.editModal.placeholders.folderName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Box>

      <FooterActions>
        <CancelButton variant="outlined" onClick={handleClose}>
          {t('resources.documentCategories.folders.editModal.buttons.cancel')}
        </CancelButton>
        <DuplicateButton variant="contained" onClick={handleDuplicate} disabled={!name.trim()}>
          {update
            ? t('resources.documentCategories.folders.editModal.buttons.save')
            : t('resources.documentCategories.folders.duplicateModal.buttons.duplicate')}
        </DuplicateButton>
      </FooterActions>
    </Box>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: 'env(safe-area-inset-bottom)',
          paddingBottom: '16px',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: '2px',
          mx: 'auto',
          my: '12px',
        }}
      />
      {formContent}
    </Drawer>
  ) : (
    <Dialog open={open} onClose={handleClose}>
      {formContent}
    </Dialog>
  );
}
